<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('general.contacts') }}
        </h6>

        <CreateButton
            :label="$t('address_book.create_partner_practices')"
            :to="{ name: 'addressBookPartnerPracticesForm' }"/>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('address_book.partner_practices') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :federalStatesDropdown="federalStatesDropdown"
            type="client"
            @search="getPartnerPractices"/>

        <PerPage
            :values="[10, 25, 50, 100]"
            @update:perPage="getPartnerPractices"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!partnerPractices?.data?.length"
            icon="fa-address-book"
            title="Keine Kontakte gefunden"
            subtitle="Es wurden keine Kontakte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click.prevent="sortBy('firstName')">
                  <span>{{ $t('general.first_name') }}</span>
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('lastName')">
                  <span>{{ $t('general.last_name') }}</span>
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click.prevent="sortBy('company')">
                  <span>{{ $t('general.company_client') }}</span>
                  <i :class="getIconClass('company')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('phoneNumber')">
                  {{ $t('general.phone_number') }}
                  <i :class="getIconClass('phoneNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('email')">
                  {{ $t('general.email') }}
                  <i :class="getIconClass('email')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('address')">
                  {{ $t('general.street') }}
                  <i :class="getIconClass('address')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('zip')">
                  {{ $t('general.postal_code') }}
                  <i :class="getIconClass('zip')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('city')">
                  {{ $t('general.city') }}
                  <i :class="getIconClass('city')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('federalState')">
                  {{ $t('general.federal_state') }}
                  <i :class="getIconClass('federalState')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('position')">
                  {{ $t('general.position') }}
                  <i :class="getIconClass('position')"></i>
                </a>
              </th>
              <th>
                {{ $t('general.info') }}
              </th>
              <th style="min-width: 130px!important; text-align: center">
                {{ $t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(partner) in partnerPractices?.data" :key="partner.id">
              <td>{{ partner?.firstName }}</td>
              <td>{{ partner?.lastName }}</td>
              <td>{{ partner?.company }}</td>
              <td>{{ partner?.phoneNumber }}</td>
              <td>{{ partner?.email }}</td>
              <td>{{ partner?.address }}</td>
              <td>{{ partner?.zip }}</td>
              <td>{{ partner?.city }}</td>
              <td>{{ partner?.federalStateTranslation }}</td>
              <td>{{ partner?.position }}</td>
              <td>{{ partner?.info ? 'JA' : 'NEIN' }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({name: 'addressBookPartnerPracticesForm', params: {id: partner?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2"
                        @click="deleteModal(partner?.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="partnerPractices?.pagination?.total_pages ?? 0 > 1"
            :totalPages="partnerPractices?.pagination?.total_pages"
            :currentPage="partnerPractices?.pagination?.current_page"
            :totalItems="partnerPractices?.pagination?.total_items"
            :perPage="partnerPractices?.pagination?.items_per_page"
            @pagechanged="getPartnerPractices"/>
      </div>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDelete">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup lang="ts">
import partnerPracticesService from '@/api/AddressBook/partnerPractices';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import Search from '../Search.vue';
import {IAddressBookFilters, IAddressBookList} from "@/interfaces/AddressBook/IAddressBook";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const {t} = useI18n();
const partnerPractices = ref<IAddressBookList>();
const federalStatesDropdown = ref([]);
const partnerId = ref<number | null>(null);
const showModal = ref(false);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

const filters = ref<IAddressBookFilters>({
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  phoneNumber: '',
  position: '',
  email: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

async function getPartnerPractices(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IAddressBookFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = partnerPractices.value?.pagination?.items_per_page;

  try {
    const {data} = await partnerPracticesService.getPartnerPractices({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    partnerPractices.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function confirmDelete() {
  try {
    await partnerPracticesService.deletePartnerPractice(partnerId.value!);
    partnerId.value = null;
    showModal.value = false;
    await getPartnerPractices();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

async function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  await getPartnerPractices();
}

async function getFederalStateDropdown() {
  federalStatesDropdown.value = getEnums.value.federalStates;
}

function deleteModal(id: number) {
  CloseToast();
  partnerId.value = id;
  showModal.value = true;
}

async function getExcelExport() {
  await downloadFile('/address-book/partner-practices/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await Promise.all([
    getPartnerPractices(),
    getFederalStateDropdown(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>